import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import { AttributeComponentRegistry } from '@tourfold/features/custom-objects/attribute-renderers';
import { TagFormSection } from '@tourfold/libraries/shared-ui';
import { TagsRelationDisplayComponent } from './tags/tags-relation-display.component';
import { TagsRelationInputComponent } from './tags/tags-relation-input.component';

/**
 * Stable slug of the built-in **Tag** system object. Every custom-object definition carries a
 * relation to this object by default (typically exposed as the `tags` property on the parent —
 * but the *target* definition is singular `tag`). The registration below makes the field
 * render as colored chips instead of the generic ID multi-select.
 *
 * Exported so tests and other system-renderer entries can reference the canonical value.
 */
export const TAGS_SYSTEM_SLUG = 'tag';

/**
 * Registers specialized renderers for relation fields that point to known **system objects**
 * (objects shipped by the platform: Tags, Location, etc.). The instance form's
 * `tf-field-renderer` resolves these through {@link AttributeComponentRegistry.resolve} by
 * matching the target definition's slug, so a contact's "tags" relation gets a colored chip
 * picker instead of the generic multi-select.
 *
 * Wire once during app bootstrap (e.g. in `apps/web-app/src/main.ts`). Renderers register
 * once at DI startup; the registry is `providedIn: 'root'`.
 *
 * Apps that don't render custom-object instance forms (admin-app, signup-app) don't need
 * this provider.
 */
export function provideSystemRenderers(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      const registry = inject(AttributeComponentRegistry);

      // Tags bundle:
      //  - picker pair → resolved by tf-field-renderer for any relation field targeting `tag`
      //  - createView  → mounted by the instance-form's breadcrumb body when the user clicks
      //                  "+ Create" on a Tags picker
      //  - createMode  → 'self-persisted' (TagFormSection POSTs to TagsApi internally and emits
      //                  the saved TagResponse; the host just reads the id and adds it to the
      //                  parent's relation selection)
      registry.registerBundle(TAGS_SYSTEM_SLUG, {
        pickerInput: TagsRelationInputComponent,
        pickerDisplay: TagsRelationDisplayComponent,
        createView: TagFormSection,
        createMode: 'self-persisted',
      });
    }),
  ]);
}
